/**
 * Globo Product Options (GPO) accessibility enhancements.
 * Event-driven only — no document MutationObserver (that locked product pages).
 */
(function () {
  const ENHANCED = 'data-gpo-a11y-enhanced';
  const OPEN_CLASS = 'expanded';

  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getFieldLabel(field) {
    if (!field) return '';
    const labelEl = field.querySelector('.gpo-label .label-content, .gpo-label span, .gpo-label');
    return (labelEl && labelEl.textContent || '').trim().replace(/\s+/g, ' ');
  }

  function getOptionText(item) {
    if (!item) return '';
    const valueEl = item.querySelector('.dropdown-item__value, .gpo-ov-title .break-word, .choicelist-item__value');
    const helptext = item.querySelector('.gpo-ov-helptext');
    const addon = item.querySelector('.addOn');
    let text = (valueEl && valueEl.textContent || '').trim().replace(/\s+/g, ' ');
    if (!text) {
      const input = item.querySelector('input[type="radio"], input[type="checkbox"]');
      text = (input && input.value) || '';
    }
    if (helptext) {
      const help = helptext.textContent.trim();
      if (help && text.indexOf(help) === -1) {
        text = text.replace(help, '').trim();
      }
    }
    if (addon) {
      text = (text + ' ' + addon.textContent.trim()).trim();
    }
    return text;
  }

  function uniqueId(prefix) {
    return prefix + '-' + Math.random().toString(36).slice(2, 9);
  }

  function enhanceDropdown(dropdown) {
    if (dropdown.getAttribute(ENHANCED) === 'dropdown') return;

    const field = dropdown.closest('.gpo-element');
    const fieldLabel = getFieldLabel(field);
    const button = dropdown.querySelector('.dropdown-button');
    const list = dropdown.querySelector('.dropdown-list');
    const items = Array.prototype.slice.call(dropdown.querySelectorAll('.dropdown-item'));

    if (!button || !list || !items.length) return;

    dropdown.setAttribute(ENHANCED, 'dropdown');

    const listId = list.id || uniqueId('gpo-dropdown-list');
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', fieldLabel || 'Options');

    button.setAttribute('tabindex', '0');
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-controls', listId);
    button.setAttribute('aria-expanded', dropdown.classList.contains(OPEN_CLASS) ? 'true' : 'false');
    button.setAttribute('aria-label', fieldLabel || 'Select option');

    const valueEl = button.querySelector('.dropdown-button__value');
    if (valueEl) {
      button.setAttribute('aria-description', 'Selected: ' + valueEl.textContent.trim());
    }

    const optionLabels = items.map(function (item) {
      const input = item.querySelector('input[type="radio"]');
      const label = item.querySelector('label');
      const text = getOptionText(item);

      if (input) {
        input.classList.add('gpo-a11y-visually-hidden');
        input.setAttribute('tabindex', '-1');
        input.setAttribute('aria-hidden', 'true');
      }

      if (label) {
        label.setAttribute('role', 'option');
        label.setAttribute('tabindex', '-1');
        label.setAttribute('aria-selected', input && input.checked ? 'true' : 'false');
        label.setAttribute('aria-label', text);
      }

      return label;
    }).filter(Boolean);

    function isOpen() {
      return dropdown.classList.contains(OPEN_CLASS);
    }

    function setExpanded(open) {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        dropdown.classList.add(OPEN_CLASS);
      } else {
        dropdown.classList.remove(OPEN_CLASS);
      }
    }

    function syncSelectedOption() {
      items.forEach(function (item, index) {
        const input = item.querySelector('input[type="radio"]');
        const label = optionLabels[index];
        if (label && input) {
          label.setAttribute('aria-selected', input.checked ? 'true' : 'false');
        }
      });
      const selected = items.filter(function (item) {
        const input = item.querySelector('input[type="radio"]');
        return input && input.checked;
      })[0];
      const text = selected ? getOptionText(selected) : '';
      if (valueEl && text) {
        button.setAttribute('aria-description', 'Selected: ' + text);
      }
      button.setAttribute('aria-expanded', isOpen() ? 'true' : 'false');
    }

    function openDropdown(focusFirst) {
      if (!isOpen()) {
        button.click();
      }
      setExpanded(true);
      syncSelectedOption();
      if (focusFirst) {
        const selected = optionLabels.filter(function (l) {
          return l.getAttribute('aria-selected') === 'true';
        })[0];
        const target = selected || optionLabels[0];
        if (target) target.focus();
      }
    }

    function closeDropdown(returnFocus) {
      if (isOpen()) {
        button.click();
      }
      setExpanded(false);
      if (returnFocus) button.focus();
    }

    function focusOption(index) {
      if (optionLabels[index]) optionLabels[index].focus();
    }

    function getFocusedOptionIndex() {
      for (let i = 0; i < optionLabels.length; i++) {
        if (optionLabels[i] === document.activeElement) return i;
      }
      return -1;
    }

    function selectOption(index) {
      const item = items[index];
      const label = optionLabels[index];
      const input = item && item.querySelector('input[type="radio"]');
      if (label) {
        label.click();
      } else if (input) {
        input.click();
      }
      syncSelectedOption();
      closeDropdown(true);
    }

    button.addEventListener('keydown', function (event) {
      const key = event.key;
      if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        if (!isOpen()) {
          openDropdown(key === 'ArrowUp' ? false : true);
          if (key === 'ArrowUp') focusOption(optionLabels.length - 1);
        } else if (key === 'ArrowDown') {
          const idx = getFocusedOptionIndex();
          focusOption(idx < 0 ? 0 : Math.min(idx + 1, optionLabels.length - 1));
        } else if (key === 'ArrowUp') {
          const idx = getFocusedOptionIndex();
          focusOption(idx < 0 ? optionLabels.length - 1 : Math.max(idx - 1, 0));
        }
      } else if (key === 'Escape' && isOpen()) {
        event.preventDefault();
        closeDropdown(true);
      }
    });

    button.addEventListener('click', function () {
      /* GPO toggles open class; sync after its handler */
      setTimeout(syncSelectedOption, 0);
    });

    optionLabels.forEach(function (label, index) {
      label.addEventListener('keydown', function (event) {
        const key = event.key;
        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          selectOption(index);
        } else if (key === 'ArrowDown') {
          event.preventDefault();
          focusOption(Math.min(index + 1, optionLabels.length - 1));
        } else if (key === 'ArrowUp') {
          event.preventDefault();
          focusOption(Math.max(index - 1, 0));
        } else if (key === 'Escape') {
          event.preventDefault();
          closeDropdown(true);
        } else if (key === 'Tab') {
          closeDropdown(false);
        }
      });

      label.addEventListener('click', function () {
        setTimeout(syncSelectedOption, 0);
      });
    });
  }

  function enhanceSwatchField(field) {
    if (field.getAttribute(ENHANCED) === 'swatches') return;

    const wrapper = field.querySelector('.gpo-ov-wrapper');
    if (!wrapper) return;
    const swatchItems = Array.prototype.slice.call(wrapper.querySelectorAll(':scope > .gpo-swatches'));
    if (!swatchItems.length) return;

    field.setAttribute(ENHANCED, 'swatches');

    const fieldLabel = getFieldLabel(field);
    const groupId = uniqueId('gpo-swatch-group');
    const labelEl = field.querySelector('.gpo-label');

    if (labelEl) {
      if (!labelEl.id) labelEl.id = groupId + '-label';
      wrapper.setAttribute('role', 'radiogroup');
      wrapper.setAttribute('aria-labelledby', labelEl.id);
    } else if (fieldLabel) {
      wrapper.setAttribute('role', 'radiogroup');
      wrapper.setAttribute('aria-label', fieldLabel);
    }

    const controls = swatchItems.map(function (item) {
      const input = item.querySelector('input[type="radio"]');
      const label = item.querySelector('label');
      const img = item.querySelector('img');
      const text = (input && input.value) || getOptionText(item) || '';

      if (!input || !label) return null;

      input.classList.add('gpo-a11y-visually-hidden');
      input.setAttribute('tabindex', '-1');

      const accessibleName = fieldLabel ? fieldLabel + ', ' + text : text;
      label.setAttribute('role', 'radio');
      label.setAttribute('aria-checked', input.checked ? 'true' : 'false');
      label.setAttribute('aria-label', accessibleName);
      label.setAttribute('tabindex', input.checked ? '0' : '-1');
      label.classList.add('gpo-a11y-swatch-control');

      if (img) {
        /* Option value as alt (e.g. "Black Metal") — not empty/decorative */
        img.setAttribute('alt', text || accessibleName || 'Option swatch');
        img.removeAttribute('aria-hidden');
      }

      return { input: input, label: label, text: text };
    }).filter(Boolean);

    function syncRadiogroup() {
      let checkedIndex = -1;
      for (let i = 0; i < controls.length; i++) {
        if (controls[i].input.checked) {
          checkedIndex = i;
          break;
        }
      }
      const activeIndex = checkedIndex === -1 ? 0 : checkedIndex;
      controls.forEach(function (control, index) {
        const checked = control.input.checked;
        control.label.setAttribute('aria-checked', checked ? 'true' : 'false');
        control.label.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
      });
      return activeIndex;
    }

    function focusControl(index) {
      if (controls[index]) controls[index].label.focus();
    }

    function selectControl(index) {
      const control = controls[index];
      if (!control) return;
      control.label.click();
      syncRadiogroup();
      focusControl(index);
    }

    controls.forEach(function (control, index) {
      control.label.addEventListener('keydown', function (event) {
        const key = event.key;
        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          selectControl(index);
        } else if (key === 'ArrowRight' || key === 'ArrowDown') {
          event.preventDefault();
          selectControl((index + 1) % controls.length);
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          event.preventDefault();
          selectControl((index - 1 + controls.length) % controls.length);
        }
      });

      control.input.addEventListener('change', syncRadiogroup);
    });

    syncRadiogroup();
  }

  function enhanceChoicelistField(field) {
    if (field.getAttribute(ENHANCED) === 'choicelist') return;

    const inputs = Array.prototype.slice.call(field.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return;

    field.setAttribute(ENHANCED, 'choicelist');
    const fieldLabel = getFieldLabel(field);

    let checkedIndex = -1;
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) {
        checkedIndex = i;
        break;
      }
    }
    const activeIndex = checkedIndex === -1 ? 0 : checkedIndex;

    inputs.forEach(function (input, index) {
      const label = field.querySelector('label[for="' + input.id + '"]') || input.closest('label');
      const text = getOptionText(label || input.parentElement) || input.value;
      if (label) {
        label.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
        label.setAttribute('role', 'radio');
        label.setAttribute('aria-checked', input.checked ? 'true' : 'false');
        label.setAttribute('aria-label', fieldLabel ? fieldLabel + ', ' + text : text);
      }
      input.classList.add('gpo-a11y-visually-hidden');
      input.setAttribute('tabindex', '-1');
    });
  }

  function enhanceGpo(root) {
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(root.querySelectorAll('.gpo-dropdown'), enhanceDropdown);
    Array.prototype.forEach.call(root.querySelectorAll('.gpo-element'), function (field) {
      if (field.querySelector('.gpo-ov-wrapper .gpo-swatches')) {
        enhanceSwatchField(field);
      } else if (field.querySelector('.choicelist-item__value')) {
        enhanceChoicelistField(field);
      }
    });
  }

  const runEnhancement = debounce(function () {
    enhanceGpo(document);
  }, 100);

  function init() {
    runEnhancement();
    /* GPO often injects after first paint — retry a few times, then stop */
    setTimeout(runEnhancement, 500);
    setTimeout(runEnhancement, 1500);
    setTimeout(runEnhancement, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('globo.options.rendered', runEnhancement);
  document.addEventListener('globo.options.change', runEnhancement);
})();
